import { useCallback, useMemo, useState } from 'react';
import { API_URL, getAccessToken, setViewAsRequestHeaders } from '../utils/api';

/**
 * "View as" preview state for admins — extracted from AuthContext.
 *
 * Two modes (mutually exclusive):
 *  - USER preview: validated against the backend's impersonation path
 *    (/auth/me + X-View-As-User). On success the view-as request headers are
 *    armed so EVERY data call is authorized and filtered as the target
 *    server-side (chariot-backend/middleware/viewAs.js) — the preview shows
 *    their real access and data, read-only, not a UI approximation.
 *  - ROLE preview: permissions computed from the catalog + company Role
 *    Defaults. The header is armed too, so permission GATES are enforced
 *    server-side; person-specific data filters still use the admin's account
 *    (a role isn't a person).
 */
// Mirror of the server's parent-permission gate (chariot-backend/permissions.js
// getPermission): a child permission is only worth what its parents are worth.
// Role preview is the one place the client resolves permissions itself instead
// of reading /auth/me's computed map, so without this the preview would show
// grants the real role could never use.
const isGranted = (v) => v === true || (typeof v === 'string' && v !== '' && v !== 'false');

function applyDependencies(values, dependencies) {
  if (!dependencies || Object.keys(dependencies).length === 0) return values;
  const resolved = { ...values };
  const settle = (key, chain) => {
    if (!isGranted(resolved[key])) return resolved[key];
    const deps = dependencies[key];
    if (!deps || deps.length === 0) return resolved[key];
    if (chain.has(key)) return resolved[key];   // hand-maintained map: guard cycles
    chain.add(key);
    for (const dep of deps) {
      if (!isGranted(settle(dep, chain))) {
        resolved[key] = false;
        return false;
      }
    }
    return resolved[key];
  };
  Object.keys(resolved).forEach((key) => settle(key, new Set()));
  return resolved;
}

export default function useViewAsPreview({
  realIsAdmin,
  user,
  permissions,
  permissionCatalog,
  permissionDependencies,
}) {
  // Role preview — admins can simulate another role to preview the UI
  const [viewAsRole, setViewAsRole] = useState(null);
  // User preview — a specific user's exact effective view
  const [viewAsUserId, setViewAsUserId] = useState(null);
  const [viewAsUserData, setViewAsUserData] = useState(null); // { user_id, role, permissions, name }

  // Effective role: prefer user preview -> role preview -> actual user role
  const effectiveRole = (realIsAdmin && viewAsUserData?.role)
    ? viewAsUserData.role
    : (realIsAdmin && viewAsRole)
      ? viewAsRole
      : user?.role;

  // Compute effective permissions:
  //   1. User preview (viewAsUserData) takes highest priority — shows that user's actual perms
  //   2. Role preview (viewAsRole) computes defaults from the catalog for that role (no overrides)
  //   3. Otherwise: the real user's computed permissions from /auth/me
  const effectivePermissions = useMemo(() => {
    if (realIsAdmin && viewAsUserData?.permissions) {
      return viewAsUserData.permissions;
    }
    if (realIsAdmin && viewAsRole && permissionCatalog.length > 0) {
      // Priority: company role override (user.role_defaults, set in
      // Settings → Role Defaults) → catalog factory default.
      const companyRoleDefaults = user?.role_defaults?.[viewAsRole] || {};
      const roleDefaults = {};
      for (const entry of permissionCatalog) {
        if (Object.prototype.hasOwnProperty.call(companyRoleDefaults, entry.key) &&
            companyRoleDefaults[entry.key] !== null && companyRoleDefaults[entry.key] !== undefined) {
          roleDefaults[entry.key] = companyRoleDefaults[entry.key];
          continue;
        }
        const defaultValue = entry.defaults?.[viewAsRole];
        if (defaultValue !== undefined) {
          roleDefaults[entry.key] = defaultValue;
        } else {
          // Fallback: false for boolean, first option for enum
          roleDefaults[entry.key] = entry.type === 'enum' && entry.options?.length > 0
            ? entry.options[0].value
            : false;
        }
      }
      return applyDependencies(roleDefaults, permissionDependencies);
    }
    return permissions;
  }, [realIsAdmin, viewAsUserData, viewAsRole, permissionCatalog, permissionDependencies, permissions, user]);

  // Start (or exit, with null) a user preview.
  const previewAsUser = useCallback(async (userId) => {
    if (!realIsAdmin) return false;
    if (!userId) {
      setViewAsUserId(null);
      setViewAsUserData(null);
      setViewAsRequestHeaders(null);
      return true;
    }
    try {
      // Deliberately bare headers + explicit view-as: this call both
      // validates the target and returns their exact effective payload.
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`,
          'X-View-As-User': userId,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setViewAsUserId(userId);
        setViewAsUserData({
          user_id: userId,
          role: data.user?.role,
          permissions: data.permissions || {},
          name: `${data.user?.first_name || ''} ${data.user?.last_name || ''}`.trim(),
        });
        // Exit role preview when switching to user preview (mutually exclusive)
        setViewAsRole(null);
        setViewAsRequestHeaders({ userId });
        return true;
      }
    } catch (err) {
      console.error('Failed to start user preview:', err);
    }
    return false;
  }, [realIsAdmin]);

  // Clearing role preview also clears user preview (mutually exclusive)
  const setViewAsRoleSafe = useCallback((role) => {
    setViewAsRole(role);
    if (role) {
      setViewAsUserId(null);
      setViewAsUserData(null);
    }
    setViewAsRequestHeaders(role ? { role } : null);
  }, []);

  return {
    viewAsRole,
    setViewAsRole: setViewAsRoleSafe,
    viewAsUserId,
    viewAsUserData,
    previewAsUser,
    effectiveRole,
    effectivePermissions,
  };
}
